import React, { useState } from "react";
import { AlertCircle, Loader, EyeOff, Eye } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import AuthLayout from "./AuthLayout";

import BackArrowIcon from "../../assets/auth/back-arrow.svg";
import UserSignInIcon from "../../assets/auth/user-sign-in.svg";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { signup } from "../../store/authSlice";
interface FormData {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  specialities: string[];
  ratePerMinute: string;
  otherSpecify: string;
  bio: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
  specialities?: string;
  ratePerMinute?: string;
  otherSpecify?: string;
  bio?: string;
  submit?: string;
}

export function SignupExpertForm(): JSX.Element {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    specialities: [],
    otherSpecify: "",
    ratePerMinute: "",
    bio: "",
  });
  const dispatch = useAppDispatch();

  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<{ [key: string]: boolean }>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(true);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(true);
  const navigate = useNavigate();
  const specialtyOptions = [
    "Full-service implementations",
    "Account setup",
    "Project-oriented workflows",
    "Process-oriented workflows",
    "Templates",
    "Reporting",
    "Admin",
    "Security",
    "Customisation",
    "Integrations & APIs",
    "New user onboarding",
    "Disaster recovery",
    "Dashboard setup",
    "Data migration",
    "Others (Specify)",
  ];

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };
  const validatePassword = (password: string): boolean => {
    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,}$/;
    return passwordRegex.test(password);
  };
  const validateField = (
    name: keyof FormData,
    value: any
  ): string | undefined => {
    switch (name) {
      case "name":
        return !value.trim() ? "*Required" : undefined;
      case "email":
        if (!value.trim()) return "*Required";
        return !validateEmail(value) ? "Invalid email format" : undefined;
      case "password":
        if (!value.trim()) return "*Required";
        return !validatePassword(value)
          ? "Password must be at least 8 characters with uppercase, lowercase, number, and special character"
          : undefined;
      case "confirmPassword":
        if (!value.trim()) return "Please confirm your password";
        return value !== formData.password
          ? "Passwords do not match"
          : undefined;
      case "specialities":
        return value.length === 0 ? "*Required" : undefined;
      case "ratePerMinute":
        if (!value) return "*Required";
        if (isNaN(value) || parseFloat(value) <= 0)
          return "Please enter a valid rate";
        return undefined;
      case "otherSpecify":
        if (
          formData.specialities.includes("Others (Specify)") &&
          !value.trim()
        ) {
          return "Please specify other specialities";
        }
        return undefined;
      case "bio":
        return !value.trim() ? "*Required" : undefined;
      default:
        return undefined;
    }
  };
  // Add character limits
  const LIMITS = {
    otherSpecify: 50,
    bio: 250,
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    // Enforce character limits
    if (name === "otherSpecify" && value.length > LIMITS.otherSpecify) {
      return;
    }
    if (name === "bio" && value.length > LIMITS.bio) {
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (touched[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: validateField(name as keyof FormData, value),
      }));
    }
  };
  const handleSpecialtyChange = (specialty: string): void => {
    const updatedSpecialties = formData.specialities.includes(specialty)
      ? formData.specialities.filter((s) => s !== specialty)
      : [...formData.specialities, specialty];

    setFormData((prev) => ({
      ...prev,
      specialities: updatedSpecialties,
    }));

    if (touched.specialities) {
      setErrors((prev) => ({
        ...prev,
        specialities: validateField("specialities", updatedSpecialties),
      }));
    }
  };
  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors((prev) => ({
      ...prev,
      [name]: validateField(
        name as keyof FormData,
        formData[name as keyof FormData]
      ),
    }));
  };
  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();

    // Validate all fields
    const newErrors: FormErrors = {};
    (Object.keys(formData) as Array<keyof FormData>).forEach((key) => {
      const error = validateField(key, formData[key]);
      if (error) newErrors[key] = error;
    });

    setErrors(newErrors);
    setTouched(
      Object.keys(formData).reduce((acc, key) => ({ ...acc, [key]: true }), {})
    );

    if (Object.keys(newErrors).length > 0) return;

    setLoading(true);
    try {
      const result = await dispatch(
        signup({
          name: formData.name,
          email: formData.email,
          password: formData.password,
          role: "Expert",
          specialities: formData.specialities,
          otherSpecify: formData.otherSpecify,
          ratePerMinute: formData.ratePerMinute,
          bio: formData.bio,
        })
      ).unwrap();

      if (result.success) {
        navigate("/signup/2FA");
      } else {
        setErrors((prev) => ({
          ...prev,
          submit: "Failed to create account. Please try again.",
        }));
      }
    } catch (error: any) {
      console.error("Failed to sign up:", error);
      if (
        error.message.includes("Firebase: Error (auth/email-already-in-use).")
      ) {
        setErrors((prev) => ({
          ...prev,
          submit:
            "Email already in use. Please sign in or use a different email.",
        }));
      } else {
        setErrors((prev) => ({
          ...prev,
          submit:
            error.message || "An unexpected error occurred. Please try again.",
        }));
      }
    } finally {
      setLoading(false);
    }
  };
  return (
    <AuthLayout>
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-row w-full shadow-lg rounded-lg overflow-hidden h-[100vh]">
          {/* Left Column: Signup Form */}
          <div className="flex-1 flex flex-col justify-center px-[83px] overflow-y-auto bg-white space-y-6 items-center py-10">
            <div className="">
              <div className="text-center">
                <div className="flex justify-center items-center gap-5">
                  <button onClick={() => navigate(-1)}>
                    <img
                      src={BackArrowIcon}
                      alt="back arrow"
                      className="h-14   text-[#919191]"
                    />
                  </button>
                  <h2 className="text-[48px] nunito-extrabold text-[#0A0A0A]">
                    Create your Expert account
                  </h2>
                </div>

                <p className="mt-2  nunito-regular text-[#696969]">
                  or{" "}
                  <Link to="/signin" className="text-[#F15D18] nunito-semibold">
                    sign in{" "}
                  </Link>
                  to your account
                </p>
              </div>
              {errors.submit && (
                <div className="rounded-md bg-red-50 p-4" role="alert">
                  <div className="flex">
                    <AlertCircle
                      className="h-5 w-5 text-[#D5332A]"
                      aria-hidden="true"
                    />
                    <div className="ml-3">
                      <p className="text-sm text-[#D5332A]">{errors.submit}</p>
                    </div>
                  </div>
                </div>
              )}
              <form className="mt-10 " onSubmit={handleSubmit}>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-sm font-medium text-[#1D1D1D] mb-1"
                    >
                      Full Name
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      // className="w-full px-4 py-2 border border-gray-300 rounded-md  focus:ring-orange-500"
                      className={`w-full px-4 py-2 border nunito-medium border-[#D9D9D9] rounded-md ${
                        touched.name && errors.name
                          ? "border-[#D5332A] text-[#D5332A] l"
                          : "ring-orange-500"
                      }`}
                      aria-invalid={
                        touched.name && errors.name ? "true" : "false"
                      }
                      aria-describedby="name-error"
                      placeholder="Enter your full name"
                    />
                    {touched.name && errors.name && (
                      <p
                        className="text-[#D5332A] text-[10px] nunito-norma mt-1"
                        id="name-error"
                        role="alert"
                      >
                        {errors.name}
                      </p>
                    )}
                  </div>
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-[#1D1D1D] mb-1"
                    >
                      Email
                    </label>
                    <input
                      id="email"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`w-full px-4 py-2 border nunito-medium border-[#D9D9D9] rounded-md ${
                        touched.email && errors.email
                          ? "border-[#D5332A] text-[#D5332A]"
                          : "ring-[#F15D18]"
                      }`}
                      // className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500"
                      placeholder="Enter your email address"
                      aria-invalid={
                        touched.email && errors.email ? "true" : "false"
                      }
                      aria-describedby="email-error"
                    />
                    {touched.email && errors.email && (
                      <p
                        className="text-[#D5332A] text-xs mt-1"
                        id="email-error"
                        role="alert"
                      >
                        {errors.email}
                      </p>
                    )}
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm nunito-semibold text-[#1D1D1D] mb-3">
                    Areas of Expertise
                  </label>
                  {touched.specialities && errors.specialities && (
                    <p className="text-[#D5332A] text-xs mt-1" role="alert">
                      {errors.specialities}
                    </p>
                  )}
                  <div className="grid grid-cols-3 gap-2">
                    {specialtyOptions.map((specialty) => (
                      <label key={specialty} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={
                            formData.specialities?.includes(specialty) ?? false
                          }
                          onChange={() => handleSpecialtyChange(specialty)}
                          className={`rounded h-[16px] w-[16px] border-[#D9D9D9] accent-[#F15D18] text-[#F15D18] focus:ring-[#F15D18] ${
                            touched.specialities && errors.specialities
                              ? "border-[#D5332A]"
                              : ""
                          }`}
                        />
                        <span className="ml-2 text-[14px] nunito-regular text-[#404040]">
                          {specialty}
                        </span>
                      </label>
                    ))}
                  </div>

                  {formData.specialities?.includes("Others (Specify)") && (
                    <div className="mt-3">
                      <label
                        htmlFor="ratePerMinute"
                        className="block text-[14px] nunito-regular text-[#1D1D1D] mb-1"
                      >
                        Others (Specify)
                      </label>
                      <div className="relative">
                        <input
                          id="otherSpecify"
                          type="text"
                          name="otherSpecify"
                          value={formData.otherSpecify}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          maxLength={LIMITS.otherSpecify}
                          className={`w-1/2 px-4 py-2 border border-[#D9D9D9] nunito-medium rounded-md ${
                            touched.otherSpecify && errors.otherSpecify
                              ? "border-[#D5332A] text-[#D5332A]"
                              : "border-[#F15D18]"
                          }`}
                          placeholder="Please mention other areas of expertise"
                        />
                        <div className="absolute bottom-[-20px] right-1/2 text-xs text-gray-500">
                          {formData.otherSpecify.length}/{LIMITS.otherSpecify}
                        </div>
                      </div>
                      {touched.otherSpecify && errors.otherSpecify && (
                        <p className="text-[#D5332A] text-xs mt-1" role="alert">
                          {errors.otherSpecify}
                        </p>
                      )}
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-6 mt-2">
                  <div>
                    <label
                      htmlFor="ratePerMinute"
                      className="block text-[14px]  text-[#1D1D1D] nunito-semibold  mb-1"
                    >
                      Rate per minute (USD)
                    </label>
                    <input
                      id="ratePerMinute"
                      name="ratePerMinute"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.ratePerMinute}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`w-full px-4 py-2 border border-[#D9D9D9] text-[#919191]  nunito-medium rounded-md ${
                        touched.ratePerMinute && errors.ratePerMinute
                          ? "border-[#D5332A] text-[#D5332A]"
                          : "border-[#D9D9D9]"
                      }`}
                      placeholder="0.00"
                    />
                    {touched.ratePerMinute && errors.ratePerMinute && (
                      <p className="text-[#D5332A] text-xs mt-1" role="alert">
                        {errors.ratePerMinute}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="bio"
                      className="block text-[14px] nunito-semibold text-[#1D1D1D] mb-1"
                    >
                      Professional Bio
                    </label>
                    <div className="relative">
                      <textarea
                        id="bio"
                        name="bio"
                        value={formData.bio}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        rows={2}
                        maxLength={LIMITS.bio}
                        className={`w-full px-4 py-2 border text-[#919191] nunito-medium rounded-md ${
                          touched.bio && errors.bio
                            ? "border-[#D5332A] text-[#D5332A]"
                            : "border-[#D9D9D9]"
                        }`}
                        placeholder="Tell us about your expertise and experience..."
                      />
                      <div className="absolute bottom-[-20px] right-0 text-xs text-gray-500">
                        {formData.bio.length}/{LIMITS.bio}
                      </div>
                    </div>
                    {touched.bio && errors.bio && (
                      <p className="text-[#D5332A] text-xs mt-1" role="alert">
                        {errors.bio}
                      </p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="password"
                      className="block text-[14px] font-medium text-[#1D1D1D] nunito-semibold mb-1"
                    >
                      Password
                    </label>
                    <div className="relative">
                      <input
                        id="password"
                        name="password"
                        type={!showPassword ? "text" : "password"}
                        value={formData.password}
                        onBlur={handleBlur}
                        onChange={(e) =>
                          setFormData({ ...formData, password: e.target.value })
                        }
                        // className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500"
                        className={`w-full px-4 py-2 border border-[#D9D9D9] text-[#919191] nunito-medium rounded-md ${
                          touched.password && errors.password
                            ? "border-[#D5332A]"
                            : "border-[#D9D9D9]"
                        }`}
                        placeholder="Password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                      >
                        {showPassword ? (
                          <EyeOff className="h-6 w-6 text-[#919191]" />
                        ) : (
                          <Eye className="h-6 w-6 text-[#919191]" />
                        )}
                      </button>
                    </div>
                    {touched.password && errors.password && (
                      <p className="text-[#D5332A] text-xs mt-1" role="alert">
                        {errors.password}
                      </p>
                    )}
                    <p className="text-[12px] text-[#919191] nunito-medium mt-2">
                      Password must be minimum 8 characters, with uppercase,
                      lowercase, a number (0-9), and a special character (!, @,
                      #, $, %, ^, &, *)
                    </p>
                  </div>

                  <div>
                    <label
                      htmlFor="confirmPassword"
                      className="block text-[14px] text-[#1D1D1D] nunito-semibold mb-1"
                    >
                      Confirm Password
                    </label>
                    <div className="relative">
                      <input
                        id="confirmPassword"
                        name="confirmPassword"
                        type={!showConfirmPassword ? "text" : "password"}
                        value={formData.confirmPassword}
                        onBlur={handleBlur}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            confirmPassword: e.target.value,
                          })
                        }
                        className={`w-full px-4 py-2 border rounded-md border-[#D9D9D9] text-[#919191] nunito-medium  focus:ring-[#F15D18] focus:border-[#F15D18]  ${
                          touched.password && errors.password
                            ? "border-[#D5332A]"
                            : "focus:ring-[#F15D18]"
                        }`}
                        placeholder="Confirm Password"
                        aria-invalid={
                          touched.name && errors.name ? "true" : "false"
                        }
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 "
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="h-6 w-6 text-[#919191]" />
                        ) : (
                          <Eye className="h-6 w-6 text-[#919191]" />
                        )}
                      </button>
                    </div>
                    {touched.confirmPassword && errors.confirmPassword && (
                      <p className="text-[#D5332A] text-xs mt-1" role="alert">
                        {errors.confirmPassword}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex flex-col items-center mt-10">
                  <button
                    type="submit"
                    disabled={loading}
                    className={`mt-2 w-[50%] flex items-center justify-center px-4 py-2 text-white font-medium rounded-md ${
                      loading ? "bg-gray-400" : "bg-[#F15D18] hover:bg-primary"
                    } focus:ring-2 focus:ring-orange-500`}
                  >
                    {loading && (
                      <Loader className="h-5 w-5 animate-spin mr-2 " />
                    )}
                    {loading ? "Signing up..." : "Sign Up"}
                  </button>
                </div>
              </form>
            </div>
          </div>
          {/* Right Column: Help Section */}
          <div className="w-1/3 flex flex-col justify-center bg-[#FBFAF9] p-12">
            <img
              src={UserSignInIcon}
              alt="Help Illustration"
              className="mx-auto mb-6"
            />
            <p className="text-[#919191] nunito-regular  text-center">
              Tell us more about yourself!
            </p>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
}
